import { makeStyles } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import * as CSS from 'csstype';

/**
 *
 * CSS rules definitions
 *
 */

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type BreakPointDef =
  | ['up' | 'down' | 'only', Breakpoint, CSSProps]
  | ['between', Breakpoint, Breakpoint, CSSProps];
type BreakPointDefs = Array<BreakPointDef>;

export type CSSProps =
  | boolean
  | CSS.Properties
  | Partial<{
      breakpoints: BreakPointDefs;
      flip: boolean;
      '&:hover': CSS.Properties;
      '&:focus': CSS.Properties;
      '&::before': CSS.Properties;
      '&::after': CSS.Properties;
      '& .MuiSelect-select': CSS.Properties;
      '& .MuiSelect-select:focus': CSS.Properties;
      '&:nth-child(1)': CSS.Properties;
      [key: string]: BreakPointDefs | CSSProps;
    }>;

/**
 *
 * MUI Component class CSS override types
 *
 * /
type ComponentClassTypes = {
  Button: ButtonClassKey;
  Tab: TabClassKey;
};
type ComponentNames = keyof ComponentClassTypes;
type ComponentClassDeclaration<K extends ComponentNames, T extends ComponentClassTypes[K]> = {
  [propKey in T]: CSSProps;
};
type ComponentClassDeclarations = {
  [key in ComponentNames]: Partial<ComponentClassDeclaration<key, ComponentClassTypes[key]>>;
};
type ComponentDeclarations = {
  [scope: string]: Partial<ComponentClassDeclarations>;
};

Example:
const comps: ComponentDeclarations = {
  megaMenu: {
    Tab: {
      selected: {
        background: 'blue'
      }
    }
  }
};
*/
/**
 *
 * Determines whether a variant is enabled in component props
 *
 */

export const isVariantEnabled = (props: any, variantName: string) => {
  return (
    (`variant-${variantName}` in props && props[`variant-${variantName}`] !== false) ||
    props.variant?.includes(variantName) ||
    false
  );
};
/**
 *
 * This function will convert serialized breakpoints for example
 *
 *  [['down', 'md', { color: 'orange' }]]
 *
 * Into objects with calls to Material UI theme breakpoints
 *
 *  {
 *    [theme.breakpoints.down(md)] : { color: 'orange' }
 *  }
 *
 */
// breakpoints:
export const convertBreakpoints = (theme: any, list: any, transform = (one: any) => one) => {
  const res: any = {};
  if (list) {
    list.forEach((items: [string, string, string, any] | [string, string, any]) => {
      if (items.length === 4) {
        const [, start, end, value] = items;
        res[theme.breakpoints.between(start, end)] = transform(value);
      } else {
        const [keyword, breakname, value] = items;
        res[theme.breakpoints[keyword](breakname)] = transform(value);
      }
    });
  }
  return res;
};

/*
 * Works like makeStyles but allows style overrides from theme
 *
 */
export const makeThemedStyles = <T extends CSSProps>(
  componentPath: string,
  themeConstructor: (theme: any) => T
): (() => ClassNameMap<Extract<keyof T, string>>) => {
  return makeStyles((theme: any) => {
    const parts = componentPath.split('.');
    const componentName = parts[parts.length - 1];
    const styledObj = theme?.disableLocalOverrides ? ({} as any) : (themeConstructor(theme) as any);
    const res: any = { [componentName]: {}, ...styledObj };

    let themeObj: any = theme.qpr || {};
    componentPath.split('.').forEach(part => {
      if (themeObj) {
        themeObj = themeObj[part];
      }
    });

    const transformObject = (original: any) => {
      if (!original) {
        return {};
      }
      const obj = { ...original };
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (value && typeof value === 'string' && value.indexOf('theme') >= 0) {
          const hasImportant = value.indexOf('!important');
          const append = hasImportant > 0 ? ' !important' : '';
          const useValue = hasImportant > 0 ? value.slice(0, hasImportant) : value;
          const localParts = useValue.split('.');
          let themeObject: any = theme;
          localParts.forEach((partKey, i) => {
            if (i === 0 || !themeObject) {
              return;
            }
            if (partKey.indexOf('(') >= 0) {
              const [fnName, argsParts] = partKey.split('(');
              const [argStr] = argsParts.split(')');

              if (typeof themeObject[fnName] === 'function') {
                obj[key] = themeObject[fnName](JSON.parse(argStr)) + append;
              }
            } else if (i === localParts.length - 1 && typeof themeObject[partKey] !== 'undefined') {
              obj[key] = themeObject[partKey] + append;
              return;
            }
            themeObject = themeObject[partKey];
          });
        }
      });
      return obj;
    };

    if (themeObj !== theme && themeObj) {
      // base style CSS and breakpoints
      Object.assign(res[componentName], transformObject(themeObj?.css));
      Object.assign(res[componentName], {
        ...convertBreakpoints(theme, themeObj.css?.breakpoints as any, transformObject)
      });

      Object.keys(themeObj)
        .filter(key => key !== 'css')
        .forEach(variantName => {
          if (!res[variantName]) res[variantName] = {};
          Object.assign(res[variantName], transformObject(themeObj[variantName]?.css));
          if (themeObj[variantName]?.css?.breakpoints) {
            Object.assign(res[variantName], {
              ...convertBreakpoints(
                theme,
                themeObj[variantName]?.css?.breakpoints as any,
                transformObject
              )
            });
          }
        });
    } else {
      // console.log('Theme was not found', componentPath, theme);
    }
    // console.log(componentPath, res);
    return res;
  }) as any;
};
